import React, { useRef, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Button, Grid } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import YupPassword from "yup-password";
import { FormLabel, TextField, CircularProgress } from "@mui/material";
import { changePassword } from "services/awsService/awsService";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

YupPassword(yup);

const RequiredLabel = ({ children }) => {
  const { t } = useTranslation();
  return (
    <FormLabel
      sx={{ display: "flex", alignItems: "center", margin: "10px auto 5px", fontSize: "15px" }}
    >
      <span style={{ color: "red", fontSize: "small", marginRight: "5px" }}>*</span>
      {children}
      <span style={{ color: "red", fontSize: "small", marginLeft: "10px" }}>
        {t("formLabels.required")}
      </span>
    </FormLabel>
  );
};

const ChangePassword = ({
  openChangePasswordDialog,
  setOpenChangePasswordDialog,
  setOpenSnackbar,
  setSeverity,
  setAlertMessage,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const schema = yup.object().shape({
    password: yup
      .string()
      .password()
      .min(8, t("alerts.password.passwordTooShort"))
      .minUppercase(1, t("alerts.password.passwordNoUppercase"))
      .minLowercase(1, t("alerts.password.passwordNoLowercase"))
      .minNumbers(1, t("alerts.password.passwordNoNumber"))
      .minSymbols(1, t("alerts.password.passwordNoSymbol"))
      .required(),
    newPassword: yup
      .string()
      .password()
      .min(8, t("alerts.password.passwordTooShort"))
      .minUppercase(1, t("alerts.password.passwordNoUppercase"))
      .minLowercase(1, t("alerts.password.passwordNoLowercase"))
      .minNumbers(1, t("alerts.password.passwordNoNumber"))
      .minSymbols(1, t("alerts.password.passwordNoSymbol"))
      .required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], t("alerts.password.passwordNotMatch"))
      .required(),
  });
  const defaultValues = {
    password: "",
    newPassword: "",
  };
  const { handleSubmit, reset, control, formState, setValue, getValues } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;

  const changePasswordHandler = (data) => {
    setLoading(true);
    // console.log("Change Password");
    // console.log("data is", data);
    changePassword(data.password, data.newPassword)
      .then((resp) => {
        setLoading(false);
        setOpenSnackbar(true);
        setSeverity("success");
        setAlertMessage(t("alerts.password.success"));
        setOpenChangePasswordDialog(false);
      })
      .catch((error) => {
        setLoading(false);
        Sentry.captureException(error);
        setOpenSnackbar(true);
        setSeverity("error");
        setAlertMessage(t("alerts.password.fail"));
        console.log(error.message);
      });
  };

  return (
    <Dialog
      open={openChangePasswordDialog}
      onClose={() => setOpenChangePasswordDialog(false)}
      maxWidth="xs" // reduced width
      fullWidth
      // PaperProps={{
      //   sx: {
      //     borderRadius: 3,
      //     boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
      //     minWidth: { xs: "90%", sm: "400px" },
      //     p: 2,
      //   },
      // }}
    >
      {/* Soft close icon */}
      <CloseIcon
        fontSize="small"
        onClick={() => setOpenChangePasswordDialog(false)}
        style={{
          color: "#6c757d",
          display: "block",
          marginLeft: "auto",
          marginRight: 4,
          marginTop: 4,
          cursor: "pointer",
        }}
      />

      {/* Title */}
      <DialogTitle 
      sx={{
        fontSize: "1.25rem",
        fontWeight: "600",
        px: 3,
        pt: 2,
        pb: 1,
        borderBottom: "1px solid #e0e0e0",
        textAlign: "center",
        }}>
        <MDTypography variant="h5" sx={{ fontWeight: 600 }}>
          {t("headings.changePassword")}
        </MDTypography>
        <MDTypography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {t("messages.profile.changePassword")}
        </MDTypography>
      </DialogTitle>

      <form onSubmit={handleSubmit(changePasswordHandler)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RequiredLabel>{t("formLabels.password")}</RequiredLabel>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    {...field}
                    variant="outlined"
                    placeholder={t("formLabels.password")}
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                            <Icon>{showPassword ? "visibility" : "visibility_off"}</Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RequiredLabel>{t("formLabels.newPassword")}</RequiredLabel>
              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message}
                    {...field}
                    variant="outlined"
                    placeholder={t("formLabels.newPassword")}
                    type={showNewPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            size="small"
                          >
                            <Icon>{showNewPassword ? "visibility" : "visibility_off"}</Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RequiredLabel>{t("formLabels.confirmPassword")}</RequiredLabel>
              <Controller
                render={({ field }) => (
                  <TextField
                    sx={{ height: 50 }}
                    size="large"
                    error={!!errors.confirmPassword}
                    helperText={errors?.confirmPassword?.message}
                    {...field}
                    inputProps={{
                      autoComplete: "new-password",
                    }}
                    InputProps={{
                      className: "pr-2",
                      type: showNewPassword ? "text" : "password",
                    }}
                    variant="outlined"
                    fullWidth
                  />
                )}
                name="confirmPassword"
                control={control}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ display: "flex", flexDirection: "column", gap: 1, px: 3, pb: 2 }}>
          <Button
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: "#10B0D5",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              "&:hover": { backgroundColor: "#089bb8", color: "#fff" },
            }}
          >
            {loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              t("buttonTitles.changePassword")
            )}
          </Button>

          <Button
            fullWidth
            type="button"
            sx={{
              backgroundColor: "#F6821E",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              "&:hover": { backgroundColor: "#d96e15", color: "#fff" },
            }}
            onClick={() => setOpenChangePasswordDialog(false)}
          >
            {t("buttonTitles.close")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePassword;
